import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Breadcrumbs from '@/components/Breadcrumbs';
import { Skeleton } from '@/components/Skeleton';
import { getAuthHeaders } from '@/data/getAuthHeaders';
import { useOrganizationId } from '@/hooks/useSwr';
import { getAgentBuilderToolServerPath } from '@/utils/constants';

interface ToolsResponse {
  tools: Array<{
    name: string;
    description?: string;
    inputSchema?: any;
  }>;
}

export function Tools() {
  const [toolsData, setToolsData] = useState<ToolsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const organizationId = useOrganizationId();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTools = async () => {
      try {
        setLoading(true);
        setError(null);

        // Call the agent-builder-tool-server MCP tools/list endpoint
        const authHeaders = await getAuthHeaders();
        const headers = {
          'Content-Type': 'application/json',
          ...authHeaders,
        };

        // Add X-Tenant-Id if organizationId is available (following same pattern as other API calls)
        if (organizationId) {
          headers['X-Tenant-Id'] = organizationId;
        }

        const response = await fetch(getAgentBuilderToolServerPath('mcp'), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            method: 'tools/list',
            params: {},
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setToolsData(data.result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch tools');
      } finally {
        setLoading(false);
      }
    };

    fetchTools();
  }, [organizationId]);

  return (
    <div>
      <div className="border-b border-secondary px-4 py-3">
        <Breadcrumbs />
      </div>
      <div className="flex flex-col gap-6 px-9 py-11">
        <div className="flex items-center gap-2">
          <span className="text-3xl">Tools</span>
        </div>

        <div className="flex flex-col gap-4">
          {loading ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-secondary bg-background p-4"
                >
                  <Skeleton className="mb-2 h-5 w-3/4" />
                  <Skeleton className="mb-3 h-4 w-full" />
                  <Skeleton className="h-8 w-24" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="rounded-md bg-red-50 p-4 text-red-800 dark:bg-red-900/20 dark:text-red-200">
              Error: {error}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {toolsData?.tools?.map((tool) => (
                <div
                  key={tool.name}
                  className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {tool.name}
                      </h3>
                      <p className="mt-2 line-clamp-2 text-sm text-gray-600">
                        {tool.description || 'No description available'}
                      </p>
                    </div>

                    <div className="pt-2">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/tools/playground/${tool.name}`)
                        }
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                      >
                        <svg
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M9.585 2.336a4.25 4.25 0 0 1 4.83 0L19.82 5.84a4.25 4.25 0 0 1 2.1 3.674v5.332a4.25 4.25 0 0 1-2.1 3.674l-5.405 3.155a4.25 4.25 0 0 1-4.83 0L4.18 18.52a4.25 4.25 0 0 1-2.1-3.674V9.514a4.25 4.25 0 0 1 2.1-3.674L9.585 2.336z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                        Playground
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
